// Module local imports
import { isHTMLElement, isNodeListOf } from 'lib/react';
import { handleJoinCode } from '../join-code-helper';
import { LinkDataLayerObject } from './_data-layer-types';
import {
  gtmDatalayerPush,
  isEmailLink,
  findParentCategory,
  getComponentType,
  findEventType,
  isExternalLink,
  isDownloadLink,
  setRelNoReffer,
} from './_helpers';

const srOnlySpan = '<span class="sr-only"> (Opens in a new tab)</span>';
const newTabIcon =
  '<span class="svg-icon inline-flex align-middle h-em ml-0.5"><svg version="1.1" xmlns="http://www.w3.org/2000/svg" ' +
  'xmlns:xlink="http://www.w3.org/1999/xlink" x="0" y="0" width="24" height="24" viewBox="0, 0, 10, 24"><path xmlns="http://www.w3.org/2000/svg" ' +
  'class="st0" d="M14.5,0h-4.29C9.76,0,9.54,0.54,9.85,0.85l1.44,1.44l-5,5c-0.39,0.39-0.39,1.02,0,1.41 ' +
  ' c0.39,0.39,1.03,0.39,1.42,0l5-5l1.44,1.44C14.46,5.46,15,5.24,15,4.79V0.5C15,0.22,14.78,0,14.5,0z M2,1C0.9,1,0,1.9,0,3v10 ' +
  ' c0,1.1,0.9,2,2,2h10c1.1,0,2-0.9,2-2V9.1c0-0.55-0.45-1-1-1s-1,0.45-1,1V13H2V3h4.4c0.55,0,1-0.45,1-1s-0.45-1-1-1H2z" fill="currentColor"/></svg></span>';
const JOIN_CODE_ACCESSOR = Symbol.for('nmdp:joinCodeAccessor');
type WindowWithJoinCodeAccessor = typeof globalThis & {
  [JOIN_CODE_ACCESSOR]?: string;
};

/**
 * Window-global helper for processing client-side link click events, which may be attached to links in sitecore content blocks.
 * Uses a global variable to access the current join code in order to avoid needing to pass join code through react context all
 * the way down to the content block level.
 * Note that this helper is only intended to be used for links within sitecore content blocks, and not for general link click handling,
 * which should be done via the withLinkHelperAttach utility function.
 * The linkClicked function will push a GTM event with the following properties:
 * - event: The type of the link click event, which is determined by the findEventType helper function based on the link element.
 * - clickText: The text of the link that was clicked, with email links having "@" replaced with "_at_".
 * - linkUrl: The URL of the link that was clicked (not included for email links).
 * - contentCategory: The content category of the link, which is determined by the findParentCategory helper function based on the link element's parent elements.
 * - componentType: The type of the component that contains the link, which is determined by the getComponentType helper function based on the link element's parent elements.
 * The attachToLink function will attach the linkClicked event handler to the onclick event of the specified link element(s), and will also set
 * the target="_blank" attribute and add screen reader text and an icon for links that are determined to be external or download links based on
 * the isExternalLink and isDownloadLink helper functions.  Additionally, it will handle join code processing via the handleJoinCode helper function,
 *  using the activeJoinCode getter to access the current join code from a global variable.
 * The detachFromAll function will remove the linkClicked event handler from the specified link element(s) and any child link elements.
 * Note that the withGtmLinkHelper function is used to create a window-global instance of the GtmLinkHelper class, which can be accessed via
 * window.gtmClickHelper.  This allows for a single instance of the helper to be used across the application, and for the activeJoinCode getter
 * to access a global variable for the join code without needing to pass it through react context.  The withLinkHelperAttach and withLinkHelperDetach
 * utility functions can be used to conveniently attach or detach the link helper to a given target element or NodeList of elements, which may be used
 * for general link click handling outside of sitecore content blocks.
 */
export interface IGtmLinkHelper {
  get activeJoinCode(): string | undefined;

  detachFromAll(target: HTMLElement | NodeListOf<HTMLElement>): void;

  /**
   * Client-side link clicked callback.  Used for links contained in a sitecore content block
   */
  linkClicked({ target }: PointerEvent): void;

  attachToLink(a: HTMLAnchorElement | NodeListOf<HTMLAnchorElement> | null): void;
}
/**
 * Extends Window object with a GtmLinkHelper
 */
export type WindowWithGtmLinkHelper = Window & {
  gtmClickHelper?: IGtmLinkHelper;
};

let LinkHelperFactory: () => IGtmLinkHelper;

/**
 * Creates a window-global GtmClickHelper and returns a strongly typed value.
 */
export const withGtmLinkHelper = (window?: Window): WindowWithGtmLinkHelper | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const myWindow = window as WindowWithGtmLinkHelper;
  myWindow.gtmClickHelper =
    myWindow.gtmClickHelper ?? (LinkHelperFactory ? LinkHelperFactory() : undefined);
  return myWindow;
};

// Stable onclick references
const StableOnLinkClick = (event: unknown) => {
  withGtmLinkHelper(window)?.gtmClickHelper?.linkClicked(event as PointerEvent);
};

/**
 * Helper class for processing client-side link click events
 */
export class GtmLinkHelper implements IGtmLinkHelper {
  /**
   * Client-side link clicked callback.  Used for links contained in a sitecore content block
   */
  linkClicked({ target }: PointerEvent) {
    if (target instanceof HTMLAnchorElement) {
      const componentType = getComponentType(target) ?? 'N/A';
      const contentCategory = findParentCategory(target) ?? 'N/A';
      const linkEventType = findEventType(target);
      if (isEmailLink(target)) {
        const clickText = target.firstChild?.nodeValue?.replace('@', '_at_');
        gtmDatalayerPush({
          event: linkEventType,
          clickText: `${clickText}`,
          contentCategory,
          componentType,
        } as LinkDataLayerObject);
      } else {
        const clickText = target.firstChild?.nodeValue;
        gtmDatalayerPush({
          event: linkEventType,
          clickText: clickText?.trim() ?? 'N/A',
          linkUrl: target.href,
          contentCategory,
          componentType,
        } as LinkDataLayerObject);
      }
    }
  }
  get activeJoinCode(): string | undefined {
    return (globalThis as unknown as WindowWithJoinCodeAccessor)?.[JOIN_CODE_ACCESSOR];
  }
  attachToLink(a: HTMLAnchorElement | NodeListOf<HTMLAnchorElement> | null): void {
    if (isNodeListOf(a)) {
      const handlerInstance = withGtmLinkHelper(window)?.gtmClickHelper ?? this;
      const boundHandler = handlerInstance.attachToLink.bind(handlerInstance);
      a.forEach(boundHandler);
      return;
    }
    // Early-exit if no link element passed
    if (!isHTMLElement<HTMLAnchorElement>(a)) {
      return;
    }
    // If we have something besides a link, then recursively search for links within it and attach to those instead
    if (a.tagName.toLowerCase() !== 'a') {
      const handlerInstance = withGtmLinkHelper(window)?.gtmClickHelper ?? this;
      a.querySelectorAll('a').forEach(handlerInstance.attachToLink.bind(handlerInstance));
      return;
    }
    // Set rel attribute for external links only
    if (isExternalLink(a) && !isDownloadLink(a)) {
      setRelNoReffer(a);
    }
    // Find all links either either have target="_blank" or appear to be external due to starting with "http"
    // Update: Additonal implementation for not showing arrow for internal links
    if (isExternalLink(a) || isDownloadLink(a)) {
      // Set to open in new tab
      a.setAttribute('target', '_blank');
      // Add Screen Reader text and new tab icon
      if (a.innerHTML.indexOf(srOnlySpan) === -1) {
        // eslint-disable-next-line no-param-reassign
        a.innerHTML = `${a.innerHTML}${srOnlySpan}${newTabIcon}`;
      }
    }
    // Process join code via activeJoinCode getter, which uses a global variable instead of react context
    handleJoinCode({ a, joinCode: this.activeJoinCode });
    // Attach our click handler to the link's onclick attribute.  Note that
    // this will overwrite existing gtm-helper onclick handlers, but keep
    // other onclick handlers intact.
    a.removeEventListener('click', StableOnLinkClick);
    a.addEventListener('click', StableOnLinkClick);
  }
  detachFromAll(target: HTMLElement | NodeListOf<HTMLElement>): void {
    if (isNodeListOf(target)) {
      target.forEach(this.detachFromAll.bind(this));
      return;
    }
    if (isHTMLElement(target)) {
      if (target.tagName.toLowerCase() !== 'a') {
        target.querySelectorAll('a').forEach(this.detachFromAll.bind(this));
        return;
      }
    }
    target?.removeEventListener('click', StableOnLinkClick);
  }
}

LinkHelperFactory = () => new GtmLinkHelper();

/**
 * Convenience function to attach link helper to a given target, which may be a single HTMLElement
 * or a NodeList of HTMLElements.  Uses type-guards to determine the type of the target and attaches
 * accordingly.
 * @param target  - The target to attach the link helper to, which may be a single HTMLElement or a NodeList of HTMLElements.
 */
export const withLinkHelperAttach = (target: HTMLElement | NodeListOf<HTMLElement>): void => {
  // If target is a node list, then loop through and attach to each elment individually
  if (isNodeListOf(target)) {
    target.forEach(withLinkHelperAttach);
    return;
  }
  // If target is a single element, then attach to it directly
  if (isHTMLElement(target)) {
    const helper = withGtmLinkHelper(window)?.gtmClickHelper ?? LinkHelperFactory();
    if (!helper) {
      throw new Error('Unable to initialize GtmLinkHelper');
    }
    helper.attachToLink(target.querySelectorAll('a'));
  }
};

/**
 * Convenience function to detach link helper from a given target, which may be a single HTMLElement
 * or a NodeList of HTMLElements.  Uses type-guards to determine the type of the target and detaches
 * accordingly.
 * @param target - The target to detach the link helper from, which may be a single HTMLElement or a NodeList of HTMLElements.
 */
export const withLinkHelperDetach = (target: HTMLElement | NodeListOf<HTMLElement>): void => {
  // If target is a node list, then loop through and detach from each elment individually
  if (isNodeListOf(target)) {
    target.forEach(withLinkHelperDetach);
    return;
  }
  // If target is a single element, then detach from it directly
  if (isHTMLElement(target)) {
    const helper = withGtmLinkHelper(window)?.gtmClickHelper ?? LinkHelperFactory();
    if (!helper) {
      throw new Error('Unable to initialize GtmLinkHelper');
    }
    helper.detachFromAll(target);
  }
};
